import {getToken, requestDelete, requestGet, requestPost, requestPut} from "../util/request";
import {LoginProps} from "../types/authority";
import {
    BoardShareItem,
    BoardShareListProps,
    InvitationCodeItem,
    InvitationStatusItem,
    verifyCaptchaItem
} from "../types/boardShare.types";
import {RegistrationAttributionPayload} from "../util/registrationAttribution";

interface LoginResponse {
    code: number;
    data: LoginProps;
    message: string;
    i18nKey?: string
}

interface ShareResponse {
    code: number;
    data: BoardShareItem;
    message: string;
    i18nKey?: string
}

interface CreateShareResponse {
    code: number;
    data: BoardShareItem;
    message: string;
    i18nKey?: string
}

interface CreateInvitationCodeResponse {
    code: number;
    data: InvitationCodeItem;
    message: string;
    i18nKey?: string
}

interface CreateInvitationStatusResponse {
    code: number;
    data: InvitationStatusItem;
    message: string;
    i18nKey?: string
}

interface registerResponse {
    code: number;
    data: string;
    message: string;
    i18nKey?: string
}

interface verifyCaptchaResponse {
    code: number;
    data: verifyCaptchaItem;
    message: string;
    i18nKey?: string
}

interface SMSResponse {
    code: number;
    data: boolean;
    message: string;
    i18nKey?: string
}

interface userConfigResponse {
    success?: boolean;
    code: number;
    data: any;
    message: string;
    i18nKey?: string
}

export const loginApi = async (email?: string, password?: string): Promise<LoginProps | null> => {
    try {
        const response = await requestPost(
            `${process.env.REACT_APP_APPLICATION_MAIN_INTERFACE}/auth/login`,
            {email, password}, true
        ) as LoginResponse;

        const { code, data, message } = response;

        if (code === 200) {
            return data;
        }

        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const loginAndRegisterApi = async (
    email?: string,
    phone?: string,
    verificationCode?: string,
    inviteCode?: string,
    local?: string,
    attribution?: RegistrationAttributionPayload | null
): Promise<LoginResponse> => {
    try {
        return await requestPost(
            `${process.env.REACT_APP_APPLICATION_MAIN_INTERFACE}/auth/loginOrRegisterWithCode`,
            {email, phone, code: verificationCode, inviteCode, local, attribution}, true
        ) as LoginResponse;

    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const registerApi = async (
    username?: string,
    password?: string,
    code?: string,
    local?: string,
    inviteCode?: string,
    attribution?: RegistrationAttributionPayload | null
): Promise<registerResponse> => {
    try {
        return await requestPost(
            `${process.env.REACT_APP_APPLICATION_MAIN_INTERFACE}/auth/register`,
            {username, password, code, local, inviteCode, attribution}, true
        ) as registerResponse;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const resetApi = async (email?: string, password?: string, code?: string, local?: string): Promise<registerResponse> => {
    try {
        return await requestPost(
            `${process.env.REACT_APP_APPLICATION_MAIN_INTERFACE}/auth/resetPassword`,
            {email, password, code, local},true
        ) as registerResponse;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const loginByFireBaseToken = async (
    token: string,
    local: string,
    attribution?: RegistrationAttributionPayload | null
): Promise<LoginProps | null> => {
    try {
        const response = await requestPost(
            `${process.env.REACT_APP_APPLICATION_MAIN_INTERFACE}/auth/replaceTokenAndRegister`,
            {token, local, attribution}, true
        ) as LoginResponse;

        const { code, data, message } = response;

        if (code === 200) {
            return data;
        }

        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }

}

export const sendCode = async (email: string, usage: 'login' | 'register' | 'code' | 'reset', captchaVerifyParam?: string): Promise<registerResponse> => {
    try {
        return await requestPost(
            `${process.env.REACT_APP_APPLICATION_MAIN_INTERFACE}/auth/verifyCaptchaAndSendCode`,
            {email, usage, captchaVerifyParam}, true
        ) as registerResponse;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const sendCodeVerifyCaptcha = async (captchaVerifyParam?: string): Promise<verifyCaptchaResponse> => {
    try {
        return await requestPost(
            `${process.env.REACT_APP_APPLICATION_MAIN_INTERFACE}/auth/verifyCaptcha`,
            {captchaVerifyParam}, true
        ) as verifyCaptchaResponse;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const sendSMS = async (phone: string, captchaVerifyParam?: string, usage?: string): Promise<SMSResponse> => {
    try {
        return await requestPost(
            `${process.env.REACT_APP_APPLICATION_MAIN_INTERFACE}/auth/sendSMS`,
            {phone, captchaVerifyParam, usage}, true
        ) as SMSResponse;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const bindCodeApi = async (token?: string, invitationCode?: string): Promise<LoginResponse> => {
    try {
        return  await requestPost(
            `${process.env.REACT_APP_APPLICATION_MAIN_INTERFACE}/auth/bindInviteCode`,
            {token, code: invitationCode}, true
        ) as LoginResponse;

    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const updateUserConfig = async (config: {useSuggestion: boolean}): Promise<userConfigResponse> => {
    try {
        return  await requestPut(
            `${process.env.REACT_APP_APPLICATION_MAIN_INTERFACE}/api/mood/userConfig`,
            config
        ) as userConfigResponse;

    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export type UserConfigData = {
    clientId: string;
    config: null | {
        useSuggestion?: boolean;
        [key: string]: any;
    };
    isClaimedDailyDrops: boolean;
    chatParameter?: string
};

export const getUserConfig = async (): Promise<UserConfigData | null> => {
    try {
        const response = await requestGet(
            `${process.env.REACT_APP_APPLICATION_MAIN_INTERFACE}/api/mood/userConfig`, true
        ) as userConfigResponse;

        const { code, data, message } = response;

        if (code === 200) {

            return data as UserConfigData;
        }

        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const checkAuthority = async (boardId: string): Promise<{useSuggestion: boolean} | null> => {
    try {
        const response = await requestGet(
            `${process.env.REACT_APP_APPLICATION_MAIN_INTERFACE}/api/mood/board/isOwnedByUser?boardId=${boardId}`, true
        ) as userConfigResponse;

        const { code, data, message } = response;

        if (code === 200) {
            return data;
        }

        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const getBoardListShare = async (boardId: string): Promise<BoardShareItem | null> => {
    try {
        const response = await requestGet(
            `/kanvas-api/api/v1/board-share/board/${boardId}`, true
        ) as ShareResponse;

        if (response) {
            const { code, data, message } = response;

            if (code === 200) {
                return data;
            }

            if (code === 401) {
                const redirectUrl = `${process.env.PUBLIC_URL || ''}/dashboard/home`;
                window.location.href = redirectUrl;
                return null;
            }
        }

        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const createBoardListShare = async (boardId: string, validDays: string): Promise<BoardShareItem | null> => {
    try {
        const response = await requestPost(
            `/kanvas-api/api/v1/board-share`, {
                boardId, validDays
            } ,true
        ) as CreateShareResponse;

        const { code, data, message } = response;

        if (code === 200) {
            return data;
        }

        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const createInvitationCode = async (): Promise<InvitationCodeItem | null> => {
    try {
        const response = await requestPost(
            `${process.env.REACT_APP_APPLICATION_MAIN_INTERFACE}/auth/createInviteCode`, {
                token: getToken()
            } ,true
        ) as CreateInvitationCodeResponse;

        const { code, data, message } = response;

        if (code === 200) {
            return data;
        }

        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const createInvitationStatus = async (): Promise<InvitationStatusItem | null> => {
    try {
        const response = await requestGet(
            `${process.env.REACT_APP_APPLICATION_MAIN_INTERFACE}/auth/inviteStats` ,true
        ) as CreateInvitationStatusResponse;

        const { code, data, message } = response;

        if (code === 200) {
            return data;
        }

        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const deleteShareId = async (shareId: string): Promise<{
    id: string,
    message: string
} | null> => {
    try {
        const response = await requestDelete(
            `/kanvas-api/api/v1/board-share/${shareId}`, true
        ) as {code: number, data: {
                id: string,
                message: string
            }, message: string};

        const { code, data, message } = response;

        if (code === 200) {
            return data;
        }

        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('Upload failed');
    }
};

export const getBoardDetailShare = async (shareId: string): Promise<BoardShareItem | null> => {
    try {
        const response = await requestGet(
            `/kanvas-api/api/v1/board-share/${shareId}`, true
        ) as CreateShareResponse;

        if (response) {
            const { code, data, message } = response;

            if (code === 200) {
                return data;
            }
        }

        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}
